/**
 * Adding a supplier, or editing one, in one pop-up.
 *
 * Jonathon, 2026-10-31: "there needs to be an option to edit the suppliers.
 * and when we click on add a new it should have a pop up box with all the info
 * to fill out." The rules it checks live in `supplierForm` (rule 5).
 *
 * Editing shows the name but never changes it — supplier names are carried as
 * text on material orders and documents (rule 8). Deactivate and add a new one.
 */

import {
  blankSupplierDraft,
  supplierDraftFields,
  supplierDraftFrom,
  supplierDraftProblem,
  supplierDraftRow,
  type PriceList,
  type Supplier,
  type SupplierDraft,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { insertRow, updateRow } from "../../lib/queries";

export function SupplierModal({
  supplier,
  existingNames,
  priceLists,
  onClose,
  onSaved,
}: {
  /** The supplier being edited. Absent when adding a new one. */
  supplier?: Supplier | undefined;
  /** Every supplier name in the table, active or not. */
  existingNames: string[];
  priceLists: PriceList[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [draft, setDraft] = useState<SupplierDraft>(() =>
    supplier ? supplierDraftFrom(supplier) : blankSupplierDraft(),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (patch: Partial<SupplierDraft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const submit = () => {
    const problem = supplierDraftProblem(draft, existingNames, supplier?.name);
    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        if (supplier) {
          await updateRow("suppliers", supplier.id, supplierDraftFields(draft));
        } else {
          await insertRow("suppliers", supplierDraftRow(draft));
        }
        await onSaved();
        onClose();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : String(caught));
        setBusy(false);
      }
    })();
  };

  return (
    <Modal
      title={supplier ? `Edit ${supplier.name}` : "New supplier"}
      onClose={onClose}
      roomy
      actions={
        <>
          <button className="btn-small" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? "Saving…" : supplier ? "Save" : "Create supplier"}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      <Row label="Name">
        {supplier ? (
          <span className="lockedval" title="Material orders carry this name">
            {supplier.name}
          </span>
        ) : (
          <input
            aria-label="Name"
            value={draft.name}
            autoFocus
            disabled={busy}
            placeholder="As it should read on a material order"
            onChange={(e) => set({ name: e.target.value })}
          />
        )}
      </Row>
      <Row label="Abbr">
        <input
          aria-label="Abbr"
          value={draft.abbr}
          disabled={busy}
          placeholder="HB, ABC…"
          onChange={(e) => set({ abbr: e.target.value })}
        />
      </Row>
      <Row label="Contact">
        <input
          aria-label="Contact"
          value={draft.primary_supplier_contact}
          disabled={busy}
          placeholder="Who to call there"
          onChange={(e) => set({ primary_supplier_contact: e.target.value })}
        />
      </Row>
      <Row label="Order email">
        <input
          aria-label="Order email"
          type="email"
          value={draft.email}
          disabled={busy}
          placeholder="Where Place Material Order sends to"
          onChange={(e) => set({ email: e.target.value })}
        />
      </Row>
      <Row label="Price sheet">
        <select
          aria-label="Price sheet"
          value={draft.price_list_id}
          disabled={busy}
          onChange={(e) => set({ price_list_id: e.target.value })}
        >
          <option value="">Catalogue cost</option>
          {priceLists.map((list) => (
            <option key={list.id} value={list.id}>
              {list.name}
            </option>
          ))}
        </select>
      </Row>

      <div className="chk-sub">Settings</div>
      <Row label="Active">
        <label className="inline">
          <input
            type="checkbox"
            aria-label="Active"
            checked={draft.active}
            disabled={busy}
            onChange={(e) => set({ active: e.target.checked })}
          />
          We buy from them now
        </label>
      </Row>
      <Row label="On account">
        <label className="inline">
          <input
            type="checkbox"
            aria-label="On account"
            checked={draft.on_account}
            disabled={busy}
            onChange={(e) => set({ on_account: e.target.checked })}
          />
          They invoice us — their purchases never appear in Receipts
        </label>
      </Row>
      <Row label="QM credit">
        <label className="inline">
          <input
            type="checkbox"
            aria-label="QM credit"
            checked={draft.credits_quickmeasure}
            disabled={busy}
            onChange={(e) => set({ credits_quickmeasure: e.target.checked })}
          />
          Credits a QuickMeasure report when we buy the material here
        </label>
      </Row>
    </Modal>
  );
}
